import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FaUniversity, FaQrcode, FaClock, FaCopy, FaDownload } from 'react-icons/fa';
import axios from 'axios';
import { IoRefresh } from 'react-icons/io5';
import Gpay from '../Assets/UPI_Logo/gpay.png';
import Bhim from '../Assets/UPI_Logo/bhim.png';
import Paytm from '../Assets/UPI_Logo/paytm.png';
import Phonepay from '../Assets/UPI_Logo/phonepay.png';

const PaymentModal = ({
  show,
  onClose,
  onProceed,
  paymentMethod,
  bankDetails,
  loading,
  amount,
  orderId,
  API_BASE_URL
}) => {
  const [timer, setTimer] = useState(10);
  const [isTimerComplete, setIsTimerComplete] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrFallback, setQrFallback] = useState('');
  const [qrAlternative, setQrAlternative] = useState('');
  const [upiId, setUpiId] = useState('');
  const [amountDisplay, setAmountDisplay] = useState('');
  const [generatingQR, setGeneratingQR] = useState(false);
  const [copied, setCopied] = useState(false);
  const [qrError, setQrError] = useState(false);
  const [currentQrIndex, setCurrentQrIndex] = useState(0);

  useEffect(() => {
    if (show && paymentMethod === 'upi' && amount > 0) {
      generateUPIQR();
    }
  }, [show, paymentMethod, amount]);

  useEffect(() => {
    if (show) {
      setTimer(10);
      setIsTimerComplete(false);
      setScreenshot(null);
      setScreenshotPreview('');
      setCopied(false);
      setQrError(false);
      setCurrentQrIndex(0);

      const interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(interval);
            setIsTimerComplete(true);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [show]);

  const generateUPIQR = async () => {
    setGeneratingQR(true);
    setQrError(false);
    try {
      const response = await axios.post(`${API_BASE_URL}generate_upi_qr.php`, {
        amount: amount,
        order_id: orderId || 'ORD' + Date.now()
      });

      console.log('QR Response:', response.data);

      if (response.data.head.code === 200) {
        const body = response.data.body;
        
        setQrCodeUrl(body.qr_code_url);
        setQrFallback(body.qr_fallback);
        setQrAlternative(body.qr_alternative);
        setUpiId(body.upi_id);
        setAmountDisplay(body.amount_display);
      } else {
        alert('Failed to generate QR code: ' + response.data.head.msg);
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('Failed to generate QR code. Please try again.');
    } finally {
      setGeneratingQR(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
        setScreenshot(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProceed = () => {
    onProceed(screenshot);
  };

  const copyUPI = () => {
    if (upiId) {
      navigator.clipboard.writeText(upiId).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      }).catch(() => {
        const textArea = document.createElement('textarea');
        textArea.value = upiId;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      });
    }
  };

  const handleQrError = () => {
    setQrError(true);
    if (currentQrIndex < 2) {
      setCurrentQrIndex(currentQrIndex + 1);
      setQrError(false);
    }
  };

  const getCurrentQrUrl = () => {
    if (currentQrIndex === 0) return qrCodeUrl;
    if (currentQrIndex === 1) return qrFallback;
    if (currentQrIndex === 2) return qrAlternative;
    return qrCodeUrl;
  };

  const retryQR = () => {
    setCurrentQrIndex(0);
    setQrError(false);
    generateUPIQR();
  };

  const isBank = paymentMethod === 'bank';

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isBank ? '🏦 Bank Transfer Payment' : '📱 UPI Payment'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isBank ? (
          // Bank Details View
          <div className="text-center mb-4">
            <div className="bank-details-modal p-4 bg-light rounded">
              <h5 className="mb-3">Please transfer the exact amount to the following account:</h5>
              <div className="text-left" style={{ textAlign: 'left' }}>
                <p><strong>Account Name:</strong> {bankDetails.account_name || 'N/A'}</p>
                <p><strong>Account Number:</strong> {bankDetails.account_no || 'N/A'}</p>
                <p><strong>IFSC Code:</strong> {bankDetails.ifsc_code || 'N/A'}</p>
                <p><strong>Branch Name:</strong> {bankDetails.branch_name || 'N/A'}</p>
                {bankDetails.bank_details && (
                  <p><strong>Additional Info:</strong> {bankDetails.bank_details}</p>
                )}
              </div>
              <div className="alert alert-info mt-3">
                <strong>ℹ️ Note:</strong> Screenshot upload is optional. You can proceed without uploading.
              </div>
            </div>
          </div>
        ) : (
          // UPI View with QR Code only
          <div className="text-center mb-4">
            <h5>Pay with UPI</h5>
            <p className="text-muted mb-1">
              Amount: <strong className="text-success">₹{amountDisplay || amount.toFixed(2)}</strong>
            </p>

            {generatingQR ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Generating QR...</span>
                </div>
                <p className="mt-2">Generating QR Code...</p>
              </div>
            ) : (
              <>
                {/* QR Code Display */}
                <div className="qr-container">
                  {getCurrentQrUrl() && !qrError ? (
                    <img
                      src={getCurrentQrUrl()}
                      alt="UPI QR Code"
                      style={{
                        maxWidth: '250px',
                        maxHeight: '250px',
                        border: '2px solid #ddd',
                        borderRadius: '8px',
                        margin: '10px auto',
                        padding: '10px',
                        backgroundColor: 'white',
                        display: 'block'
                      }}
                      onError={handleQrError}
                    />
                  ) : (
                    <div className="qr-fallback p-4" style={{
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      margin: '10px auto',
                      padding: '20px',
                      backgroundColor: '#f8f9fa',
                      maxWidth: '300px'
                    }}>
                      <FaQrcode size={60} className="text-muted mb-3" />
                      <p className="text-muted">QR Code not available</p>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={retryQR}
                      >
                        <IoRefresh /> Retry
                      </button>
                    </div>
                  )}
                </div>

                {/* UPI ID with Copy Button */}
                <div className="upi-details mt-3">
                  <div className="alert alert-info" style={{ fontSize: '14px' }}>
                    <strong>📱 Scan QR Code</strong> with any UPI app to pay
                  </div>

                  <div className="d-flex justify-content-center align-items-center gap-2 flex-wrap p-3 bg-light rounded">
                    <span className="text-muted">UPI ID:</span>
                    <code style={{ 
                      fontSize: '16px', 
                      fontWeight: 'bold',
                      background: '#fff',
                      padding: '5px 12px',
                      borderRadius: '4px'
                    }}>
                      {upiId}
                    </code>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={copyUPI}
                      title="Copy UPI ID"
                    >
                      {copied ? '✅ Copied!' : <FaCopy />}
                    </button>
                  </div>

                  <p className="text-muted mt-2" style={{ fontSize: '13px' }}>
                    Or copy the UPI ID and pay manually from your UPI app
                  </p>

                  {/* UPI Apps Logos */}
                  <div className='upi-apps mt-3'>
                    <img src={Gpay} alt="Google Pay" style={{ height: '30px', margin: '0 5px' }} />
                    <img src={Phonepay} alt="PhonePe" style={{ height: '30px', margin: '0 5px' }} />
                    <img src={Paytm} alt="Paytm" style={{ height: '30px', margin: '0 5px' }} />
                    <img src={Bhim} alt="BHIM UPI" style={{ height: '30px', margin: '0 5px' }} />
                  </div>

                  <div className="alert alert-success mt-2" style={{ fontSize: '12px' }}>
                    <strong>✅ Works with all UPI apps:</strong> Google Pay, PhonePe, Paytm, BHIM, and more
                  </div>

                  {qrError && (
                    <div className="alert alert-warning mt-2" style={{ fontSize: '12px' }}>
                      <strong>Note:</strong> QR code is having issues. Please copy the UPI ID above and pay manually.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Screenshot Upload Section - Optional for both methods */}
        <div className="mb-4">
          <h6>📷 Upload Payment Screenshot <span className="text-muted">(Optional)</span></h6>
          <Form.Group>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              disabled={!isTimerComplete}
            />
            {screenshotPreview && (
              <div className="mt-2">
                <img
                  src={screenshotPreview}
                  alt="Payment Screenshot"
                  style={{ maxWidth: '150px', maxHeight: '150px', border: '1px solid #ddd', borderRadius: '5px' }}
                />
                <p className="text-success mt-1">✅ Screenshot uploaded successfully</p>
              </div>
            )}
            <Form.Text className="text-muted">
              Upload screenshot of your {isBank ? 'bank transfer' : 'UPI payment'} confirmation (Optional)
            </Form.Text>
          </Form.Group>
        </div>

        {/* Timer */}
        <div className="text-center">
          <div className="timer-display p-2 bg-light rounded">
            <FaClock className="me-2" />
            <span className={`fw-bold ${isTimerComplete ? 'text-success' : 'text-muted'}`}>
              {isTimerComplete ? (
                '✅ You can proceed with your order'
              ) : (
                `Please wait ${timer} seconds before proceeding`
              )}
            </span>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleProceed}
          disabled={!isTimerComplete || loading || (paymentMethod === 'upi' && !upiId)}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Processing...
            </>
          ) : (
            'Proceed to Order'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PaymentModal;